// Phase 15 Plan 09 Task 2 — TournamentStandingsList.
//
// DEC-012 — Swiss-5 tournaments. Standings 3/1/0 with tiebreak chain:
// tokens → head-to-head → Buchholz. Rendered as a 6-column grid:
//   rank | name | points | tokens (tertiary) | H2H (tertiary) | Buchholz (tertiary)
//
// The tiebreaker columns (tokens, H2H, Buchholz) use --color-text-tertiary
// + smaller font (12px) per UI-SPEC §"Standings". Numeric columns set
// `font-feature-settings: "tnum" 1` so digits align in a monospace cadence.
//
// Caller's row gains a 4px Sun-Yellow left edge as a "you" indicator (UX
// affordance only — server data is unchanged). The `callerUuid` prop is
// optional; when null, no row is highlighted.
//
// The TournamentParticipant wire shape from the OpenAPI schema is the
// minimum source. The optional `head_to_head` and `buchholz` fields are
// rendered when present; legacy payloads that don't carry them render
// '—' in the tiebreaker cell.

import type { JSX } from 'react'
import { FormattedMessage } from 'react-intl'

interface Participant {
  readonly user_uuid: string
  readonly display_name: string
  readonly score: number
  readonly tokens_earned: number
  /** Optional — DEC-012 tiebreaker, not yet in TournamentParticipant wire. */
  readonly head_to_head?: number
  /** Optional — DEC-012 tiebreaker, not yet in TournamentParticipant wire. */
  readonly buchholz?: number
}

export interface TournamentStandingsListProps {
  readonly participants: readonly Participant[]
  /** When non-null + matching a row, that row gains the Sun-Yellow left edge. */
  readonly callerUuid: string | null
}

const tertiaryCell: React.CSSProperties = {
  fontFamily: 'var(--font-family-body)',
  fontWeight: 500,
  fontSize: 'var(--font-size-12)',
  color: 'var(--color-text-tertiary)',
  fontFeatureSettings: '"tnum" 1',
  textAlign: 'right',
  padding: 'var(--space-1) var(--space-2)',
}

const primaryCell: React.CSSProperties = {
  fontFamily: 'var(--font-family-body)',
  fontWeight: 600,
  fontSize: 'var(--font-size-14)',
  color: 'var(--color-text-primary)',
  fontFeatureSettings: '"tnum" 1',
  padding: 'var(--space-2)',
}

export function TournamentStandingsList(
  props: TournamentStandingsListProps,
): JSX.Element {
  const { participants, callerUuid } = props
  // Rank participants by score desc; the server returns them in this order
  // already in tournament/view, but we don't rely on it — the rank cell
  // value follows array order for the UX, and the server-authoritative
  // order is whatever it returns (DEC-021).
  const rows = participants.map((p, idx) => ({ p, rank: idx + 1 }))

  return (
    <section
      aria-labelledby="tournament-standings-heading"
      data-testid="tournament-standings"
      className="sub-card"
      style={{
        // Folk `.sub-card` recipe owns the surface (paper-2 + warm inset rule).
        width: '100%',
        borderRadius: 'var(--radius-lg)',
        overflow: 'hidden',
      }}
    >
      <header
        id="tournament-standings-heading"
        style={{
          padding: 'var(--space-2) var(--space-3)',
          fontFamily: 'var(--font-family-display)',
          fontWeight: 700,
          fontSize: 'var(--font-size-14)',
          color: 'var(--color-text-primary)',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          backgroundColor: 'var(--color-surface-mid)',
        }}
      >
        <FormattedMessage
          id="social.tournaments.standings.title"
          defaultMessage="Standings"
        />
      </header>
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
        }}
      >
        <thead>
          <tr>
            <th scope="col" style={primaryCell}>
              #
            </th>
            <th scope="col" style={primaryCell}>
              <FormattedMessage
                id="social.tournaments.standings.name"
                defaultMessage="Name"
              />
            </th>
            <th scope="col" style={{ ...primaryCell, textAlign: 'right' }}>
              <FormattedMessage
                id="social.tournaments.standings.points"
                defaultMessage="Points"
              />
            </th>
            <th scope="col" style={tertiaryCell}>
              <FormattedMessage
                id="social.tournaments.standings.tokens"
                defaultMessage="Tokens"
              />
            </th>
            <th scope="col" style={tertiaryCell}>
              <FormattedMessage
                id="social.tournaments.standings.h2h"
                defaultMessage="H2H"
              />
            </th>
            <th scope="col" style={tertiaryCell}>
              <FormattedMessage
                id="social.tournaments.standings.buchholz"
                defaultMessage="Buchholz"
              />
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ p, rank }) => {
            const isSelf = callerUuid !== null && p.user_uuid === callerUuid
            return (
              <tr
                key={p.user_uuid}
                data-self={isSelf ? 'true' : 'false'}
                data-testid={`standings-row-${p.user_uuid}`}
                style={{
                  borderLeft: isSelf
                    ? '4px solid var(--color-player-a)'
                    : '4px solid transparent',
                  backgroundColor: isSelf
                    ? 'var(--color-player-a-tint)'
                    : 'transparent',
                }}
              >
                <td style={primaryCell}>{rank}</td>
                <td style={primaryCell}>{p.display_name}</td>
                <td style={{ ...primaryCell, textAlign: 'right' }}>
                  {p.score}
                </td>
                <td style={tertiaryCell}>{p.tokens_earned}</td>
                <td style={tertiaryCell}>{p.head_to_head ?? '—'}</td>
                <td style={tertiaryCell}>{p.buchholz ?? '—'}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </section>
  )
}
